import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import CtaSection from "@/components/home/CtaSection";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { iconFor } from "@/components/home/WhatWeBuild";
import NotFound from "@/pages/NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { data: service, isLoading, isError } = useQuery({
    queryKey: ["service", slug],
    queryFn: async () => (await api.get(`/content/services/${slug}`)).data,
    retry: false,
  });

  if (isError) return <NotFound />;
  if (isLoading || !service) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center" data-testid="service-loading">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  const Icon = iconFor(service.icon);

  return (
    <>
      <Seo
        title={`${service.title} | LogiTech Nexus`}
        siteName="LogiTech Nexus"
        description={service.tagline || service.description?.slice(0, 155)}
      />
      <PageHero
        eyebrow="Services"
        title={service.title}
        subtitle={service.tagline}
        testid="service-detail-hero"
      />

      <section className="bg-void pb-28" data-testid="service-detail-body">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <div className="rounded-2xl border border-slate-800/80 bg-navy/60 p-8 sm:p-10">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-blue-500/25 bg-blue-500/10 text-blue-400">
                <Icon size={26} />
              </span>
              {service.description && (
                <p className="mt-8 text-base leading-relaxed text-slate-300 sm:text-lg">
                  {service.description}
                </p>
              )}
              {service.bullets?.length > 0 && (
                <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                  {service.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-slate-300">
                      <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-blue-400" />
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Reveal>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              data-testid="service-detail-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white shadow-[0_0_28px_rgba(59,130,246,0.4)] transition-[background-color,transform] duration-300 hover:bg-blue-500 active:scale-[0.98]"
            >
              Start a Project
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              data-testid="service-detail-back"
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-400 transition-colors hover:text-white"
            >
              <ArrowLeft size={15} />
              All services
            </Link>
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
